<template>
  <div>
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">
              <div>
                Search Results
              </div>
            </div>
          </template>
        </es-header>
      </div>
      <es-filter v-if="!loadingSearch && !loadingSearchError && prevBatchSearch
        && !show" :defaultExpanded="true">
        <template #filter-options>
          <div class="columns multi-dropdown-columns">
            <div class="column is-2">
            <es-select
              :clear-out="true"
              keyValue="id"
              label="Processing Stage :"
              :multi="true"
              :pre-selected="statusInput"
              ref="selStatuses"
              :set-values="statuses"
              valueTpl="${description}"
              @update:values="statusInput = $event"
            />
            </div>
          </div>
          <div style="padding: 1rem">
            <div class="field is-grouped flex-justify--end">
              <div class="control is-flex flex-align--center">
                <a @click.prevent="resetFilter"
                  @keypress="resetFilter"
                  tabindex="0" role="link"
                >
                  Reset
                </a>
              </div>
              <div class="control">
                <button class="button is-primary" @click="filterData">
                  Filter Data
                </button>
              </div>
            </div>
          </div>
        </template>
      </es-filter>
      <div class="table-wrapper">
        <es-loader v-if="loadingSearch" style="margin: 6.5rem" />
        <es-exception v-else-if="show" style="padding: 6.5rem" margin="0rem" size="4x"
          exceptionMessage="Search criteria must be entered to view results." />
        <es-exception v-else-if="loadingSearchError" style="padding: 6.5rem" margin="0rem" size="4x"
          exceptionMessage="There was an issue. Please contact the admin" />
        <es-exception v-else-if="!show && !this.searchBatches.length && !loadingSearch
          && !loadingSearchError" style="padding: 6.5rem" margin="0rem" size="4x"
          exceptionMessage="No results were found." />
          <table v-else  class="table is-fullwidth is-hoverable">
            <es-sorter
              :columnHeaders="columnHeaders"
              @updateSorter="updateSorter"
            />
            <tbody v-if="loadingSearch">
              <td class="td-alert" colspan="100">
                <es-loader style="margin: 4.5rem"/>
              </td>
            </tbody>
            <tbody v-else-if="loadingSearchError">
              <td class="td-alert" colspan="100">
                <es-exception
                exceptionMessage="There was an issue. Please try again or contact your
                administrator."
                margin="4.rem"
                />
              </td>
            </tbody>
            <tbody v-else-if="!searchBatches.length">
              <td class="td-alert" colspan="100">
                <es-exception
                  exceptionMessage="No results found."
                  margin="3rem"
                />
              </td>
            </tbody>
            <template v-else>
              <pid-search-batch-row
                v-for="search in this.searchBatches"
                :key="search.id"
                :search="search"
                :isMobile="isMobile"
                @update:search="onSearchRowUpdate(search.id, $event)"
              />
            </template>
          </table>
      </div>
      <div class="pager-container" v-if="!loadingSearch && totalCount">
        <es-pager :totalCount="totalCount"/>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faEllipsisV,
} from '@fortawesome/pro-light-svg-icons';
import {
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsFilter from '@/components/EsFilter.vue';
import EsSelect from '@/components/EsSelect.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import PidSearchBatchRow from '@/components/PidSearchBatchRow.vue';
import EsSorter from '@/components/EsSorter.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import { isMobile } from '@/mixins/Utils';

library.add(
  faEllipsisV,
  faExclamationTriangle,
);

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
    EsSelect,
    EsFilter,
    EsPager,
    EsSorter,
    PidSearchBatchRow,
  },
  props: {
    formDataBody: {
      type: FormData,
    },
    searchParams: {
      type: Object,
    },
    searchTypeCode: {
      type: String,
    },
    searchTypeType: {
      type: String,
    },
    page: {
      type: Number,
    },
    sortField: {
      type: String,
    },
    sortOrder: {
      type: String,
    },
    show: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loadingSearch: false,
      loadingSearchError: null,
      searchBatches: [],
      columnHeaders: [],
      statuses: [],
      statusInput: [],
      totalCount: null,
      isMobile: isMobile(),
    };
  },
  created() {
    this.searchBatchObject = this.searchBatches;
    this.columnHeaders = [
      {
        header: null,
        field: null,
      },
      {
        header: 'Search Info',
        title: 'Search Info',
        field: 'id',
        sortable: true,
        order: 'DESC',

      },
      {
        header: 'Location Info',
        title: 'Location Info',
        field: 'districtCode',
        sortable: true,
        order: null,
        hideForTablet: isMobile(),
        hideForMobile: isMobile(),
      },
      {
        header: 'Process Info',
        title: 'Process Info',
        field: 'lastUpdateDate',
        sortable: true,
        order: null,

      },
      {
        header: 'Record Info',
        title: 'Record Info',
        field: null,

      },
      {
        header: 'Status Info',
        title: 'Status Info',
        field: 'status',
        sortable: true,
        order: null,
        hideForMobile: isMobile(),
      },
      {
        header: 'Actions',
        title: 'Actions',
        field: null,
      },
    ];
    this.statuses = [
      { status: 'COMPLETED', description: 'Completed', id: 1 },
      { status: 'ERRORED', description: 'Pending: Errors', id: 2 },
      { status: 'ERRORED_VALIDATION', description: 'Pending: Validation Errors', id: 3 },
      { status: 'ERRORED_MATCHING', description: 'Pending: Matching Errors', id: 4 },
      { status: 'QUEUED', description: 'Pending: Request Queued', id: 5 },
      { status: 'LOADED', description: 'Pending: Request Loaded', id: 6 },
      { status: 'VALIDATION_IN_PROGRESS', description: 'Pending: Validation In Progress', id: 7 },
      { status: 'VALIDATION_CANCELED', description: 'Completed: Validation Canceled', id: 8 },
      { status: 'VALIDATION_COMPLETE', description: 'Pending: Validation Completed', id: 9 },
    ];
    this.statuses.filter(s => s.status);
  },
  watch: {
    show() {
      if (this.show === true) {
        this.clearFilters();
      }
    },
    $route() {
      if (this.show === false) {
        if (this.isSearchByFile && this.searchTypeCode === 'prevSearch') {
          if (this.searchTypeType === 'searchFiles') {
            this.doPrevSearch();
          } else if (this.searchTypeType === 'locationFiles') {
            this.doLocationFilesSearch();
          }
        }
        if (this.isSearchByFile && this.searchTypeCode === 'newSearch') {
          this.doFileSearch();
        }
      }
    },
  },
  computed: {
    prevBatchSearch() {
      return this.searchTypeCode === 'prevSearch';
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    prevSearchParams() {
      const params = {};
      params.personTypeCode = this.searchParams.personType
        ? this.searchParams.personType : undefined;
      params.ids = this.searchParams.id ? this.searchParams.id : undefined;
      params.startDate = this.searchParams.startDate ? this.searchParams.startDate : undefined;
      params.endDate = this.searchParams.endDate ? this.searchParams.endDate : undefined;
      params.statuses = this.statusInput.map(s => s.status);
      params.page = this.page ? this.page : undefined;
      const sortField = this.$route.query.sortField || this.sortField;
      const sortOrder = this.$route.query.sortOrder || this.sortOrder;

      params.sort = JSON.stringify([
        {
          field: sortField,
          order: sortOrder,
        },
      ]);
      return params;
    },
    filterSortParams() {
      const params = {};
      if (this.$route.query.id) {
        params.ids = this.$route.query.id;
      }
      if (this.$route.query.startDate) {
        params.startDate = this.$route.query.startDate;
      }
      if (this.$route.query.endDate) {
        params.endDate = this.$route.query.endDate;
      }
      if (this.$route.query.personType) {
        params.personType = this.$route.query.personType;
      }
      if (this.statusInput) {
        params.statuses = this.statusInput.map(s => s.status);
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    isSearchByFile() {
      if (this.$route.name === 'PidSearchFile') {
        return true;
      }
      return false;
    },
  },
  methods: {
    clearFilters() {
      this.$refs.selStatuses.clear();
    },
    async doFileSearch() {
      try {
        this.loadingSearch = true;
        this.loadingSearchError = false;
        const response = await axios.post('person-search/by-file', this.formDataBody);
        this.searchBatches = [response.data];
        this.totalCount = null;
      } catch (error) {
        this.loadingSearchError = true;
        this.showNotification('danger', [(error.response && error.response.data && error.response.data.message) || 'An unexpected error occurred.']);
      } finally {
        this.loadingSearch = false;
      }
    },
    async doPrevSearch() {
      try {
        this.loadingSearch = true;
        this.loadingSearchError = false;
        const response = await axios.get('person-search/by-file',
          {
            params: { ...this.prevSearchParams, filterByUserId: true },
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
        this.searchBatches = response.data.data;
        this.totalCount = response.data.totalCount;
      } catch (error) {
        this.loadingSearchError = true;
        this.showNotification('danger', [(error.response && error.response.data && error.response.data.message) || 'An unexpected error occurred.']);
      } finally {
        this.loadingSearch = false;
      }
    },
    async doLocationFilesSearch() {
      try {
        this.loadingSearch = true;
        this.loadingSearchError = false;
        const response = await axios.get('person-search/by-file',
          {
            params: this.prevSearchParams,
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
        this.searchBatches = response.data.data;
        this.totalCount = response.data.totalCount;
      } catch (error) {
        this.loadingSearchError = true;
        this.showNotification('danger', [(error.response && error.response.data && error.response.data.message) || 'An unexpected error occurred.']);
      } finally {
        this.loadingSearch = false;
      }
    },
    resetFilter() {
      this.$refs.selStatuses.clear();
    },
    filterData() {
      this.$router.replace({ query: this.filterSortParams });
    },
    updateSorter() {
      this.$router.replace({ query: this.filterSortParams });
    },
    onSearchRowUpdate(id, updatedSearch) {
      if (!updatedSearch) return;
      const idx = this.searchBatches.findIndex(s => s && s.id === id);
      if (idx !== -1) {
        this.$set(this.searchBatches, idx, updatedSearch);
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    max-width: 100%
    white-space: nowrap
  table td
    max-width: 100%
    white-space: wrap
  .table-wrapper
    overflow-x: auto
    white-space: nowrap
  .pager-container
    border-bottom: 1px solid #dee2e6
    border-width: 1px 0 0
    padding: 5.6px 12px
    background-color: #f9f9f9
</style>
